// src/components/Notificacoes/NotificacoesRealtime.jsx
import React, { useEffect, useRef } from "react";
import { collection, limit, onSnapshot, orderBy, query } from "firebase/firestore";
import { db } from "../../config/firebase";
import { useAuth } from "../../contexts/AuthContext";
import { useEmpresa } from "../../contexts/EmpresaContext";
import { useToast } from "../../contexts/ToastContext";
import { useNavigate } from "react-router-dom";
import { nivelUsuario } from "../../utils/permissoes";
import { NIVEIS } from "../../config/niveis";

function isDesktop() {
  if (typeof window === "undefined") return false;
  // 768px cobre praticamente qualquer desktop/tablet em landscape.
  return window.matchMedia ? window.matchMedia("(min-width: 768px)").matches : window.innerWidth >= 768;
}

function num(v, fallback = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

export default function NotificacoesRealtime() {
  const { usuario } = useAuth();
  // ⚠️ Segurança: evita quebra caso o Provider não esteja pronto.
  const empresaCtx = useEmpresa() || {};
  const modoGlobal = !!empresaCtx.modoGlobal;
  const empresaAtual = empresaCtx.empresaAtual || null;
  const { info, error } = useToast();
  const navigate = useNavigate();

  // ✅ Só Gestor e Admin recebem essas notificações
  const nivel = usuario ? nivelUsuario(usuario) : 0;
  const autorizado = nivel >= NIVEIS.ADMIN;

  const estoqueCacheRef = useRef(new Map()); // materialId -> { below: bool, qty: number }
  const estoqueReadyRef = useRef(false);
  const osReadyRef = useRef(false);
  // Baseline do servidor (evita notificar "OS antigas" por diferença de relógio do PC)
  const osBaselineMsRef = useRef(null);

  // IMPORTANTE: Não vamos mostrar toasts de ERRO ao abrir o app.
  // Se faltar índice/permissão, o console já vai registrar e o app segue normal.

  function passaEscopoEmpresa(d) {
    if (modoGlobal) return true;
    if (empresaAtual?.id) return d.empresaId === empresaAtual.id;
    if (empresaAtual?.nome) return d.empresaNome === empresaAtual.nome;
    return true;
  }

  // (removido) Auto-teste de toast — não mostrar nada ao abrir o sistema

  // -------------------------
  // 1) Notificar Nova OS
  // -------------------------
  useEffect(() => {
    if (!usuario?.grupoId) return;
    if (!autorizado) return;
    if (!isDesktop()) return;

    // ✅ Para NÃO exigir índice composto, a query não filtra por grupo/empresa.
    // As regras do Firestore devem bloquear documentos fora do grupo do usuário.
    // A filtragem de empresa (quando não-global) fica no cliente.
    const q = query(collection(db, "ordensServico"), orderBy("criadoEm", "desc"), limit(50));

    const unsub = onSnapshot(
      q,
      (snap) => {
        // Primeira carga: não notifica (senão spamma com OS antigas)
        if (!osReadyRef.current) {
          // Como a query está em ordem desc, o primeiro doc (se existir) é o mais recente.
          const first = snap.docs?.[0]?.data?.() || null;
          const ms = first?.criadoEm && typeof first.criadoEm.toMillis === "function" ? first.criadoEm.toMillis() : null;
          osBaselineMsRef.current = Number.isFinite(ms) ? ms : Date.now();
          osReadyRef.current = true;
          return;
        }

        snap.docChanges().forEach((ch) => {
          if (ch.type !== "added") return;
          const d = ch.doc.data() || {};

          // Segurança extra (caso regras permitam mais de um grupo no futuro)
          if (d.grupoId && d.grupoId !== usuario.grupoId) return;
          if (!passaEscopoEmpresa(d)) return;

          // Só notifica as que foram criadas DEPOIS do baseline (evita falso-positivo por clock skew)
          const criadoEm = d.criadoEm;
          if (!criadoEm || typeof criadoEm.toMillis !== "function") return;
          const criadoMs = criadoEm.toMillis();
          const baseline = osBaselineMsRef.current;
          if (baseline != null && criadoMs <= baseline) return;
          // Atualiza baseline para não notificar duas vezes se o listener reconectar
          osBaselineMsRef.current = Math.max(Number(baseline ?? 0), criadoMs);

          const codigo = d.codigo || "OS";
          const maquina = d.maquinaNome || d.setorNome || "-";
          const crit = d.criticidade || "-";
          const titulo = d.titulo || d.defeitoNome || "Nova OS";
          const msg = `🛠️ Nova OS aberta: ${codigo} • ${maquina} • ${crit} — ${titulo}`;

          info(msg, {
            durationMs: 9000,
            onClick: () => navigate(`/ordens-servico/editar/${ch.doc.id}`),
          });
        });
      },
      (err) => {
        console.warn("Falha ao escutar OS em tempo real:", err);
      }
    );

    return () => unsub();
  }, [usuario?.grupoId, autorizado, modoGlobal, empresaAtual?.id, empresaAtual?.nome, info, navigate]);

  // -------------------------
  // 2) Notificar Estoque abaixo do mínimo (cruzamento)
  // -------------------------
  useEffect(() => {
    if (!usuario?.grupoId) return;
    if (!autorizado) return;
    if (!isDesktop()) return;

    // ✅ Para NÃO exigir índice composto, a query não filtra por grupo/empresa.
    // As regras do Firestore devem bloquear documentos fora do grupo do usuário.
    // A filtragem de empresa (quando não-global) fica no cliente.
    const q = query(collection(db, "estoqueInterno"), orderBy("atualizadoEm", "desc"), limit(200));

    const unsub = onSnapshot(
      q,
      (snap) => {
        // Primeira carga: apenas preenche cache (não notifica)
        if (!estoqueReadyRef.current) {
          snap.docs.forEach((docu) => {
            const d = docu.data() || {};

            if (d.grupoId && d.grupoId !== usuario.grupoId) return;
            if (!passaEscopoEmpresa(d)) return;

            const qty = num(d.estoque ?? d.quantidadeAtual ?? 0, 0);
            const min = num(d.quantidadeMinima ?? d.estoqueMinimo ?? null, NaN);
            const below = Number.isFinite(min) ? qty < min : false;
            estoqueCacheRef.current.set(docu.id, { below, qty });
          });
          estoqueReadyRef.current = true;
          return;
        }

        snap.docChanges().forEach((ch) => {
          if (ch.type !== "modified" && ch.type !== "added") return;
          const d = ch.doc.data() || {};

          if (d.grupoId && d.grupoId !== usuario.grupoId) return;
          if (!passaEscopoEmpresa(d)) return;

          const qty = num(d.estoque ?? d.quantidadeAtual ?? 0, 0);
          const min = num(d.quantidadeMinima ?? d.estoqueMinimo ?? null, NaN);
          if (!Number.isFinite(min)) return;

          const nowBelow = qty < min;
          const prev = estoqueCacheRef.current.get(ch.doc.id);
          const prevBelow = prev ? !!prev.below : false;

          // Só notifica quando CRUZA o limite (de ok -> abaixo)
          if (nowBelow && !prevBelow) {
            const nome = d.produto || d.modelo || d.nome || d.codigo || "Material";
            const msg = `📦 Estoque abaixo do mínimo: ${nome} (Qtd ${qty} < Mín ${min})`;
            error(msg, {
              durationMs: 12000,
              onClick: () => navigate("/estoque/estoque-interno"),
            });
          }

          estoqueCacheRef.current.set(ch.doc.id, { below: nowBelow, qty });
        });
      },
      (err) => {
        console.warn("Falha ao escutar Estoque em tempo real:", err);
      }
    );

    return () => unsub();
  }, [usuario?.grupoId, autorizado, modoGlobal, empresaAtual?.id, empresaAtual?.nome, error, navigate]);

  return null;
}
